import { action, atom } from '@reatom/framework'
import { withLocalStorage } from '@reatom/persist-web-storage'

import { STORAGE_PREFIX } from '@root/shared/constants'
import * as UITheme from '@root/shared/ui-kit/theme'

import {
  CustomTheme,
  customThemeColorsFromBaseTheme,
  patchCustomThemeColors,
  type CustomThemeColors,
} from '../model/custom-theme'
import * as Theme from '../model/theme'
import { ThemeId } from '../model/theme-id'

import type { Appearance } from '@blade/ui-theme'

const STORAGE_KEY = `${STORAGE_PREFIX}/v1/current-custom-theme`

const DEFAULT_CUSTOM_THEME = new CustomTheme({
  baseTheme: ThemeId.Deluge,
  customColors: {},
  appearance: 'dark',
  appearanceColor: '#000000',
})

export const currentCustomThemeAtom = atom<CustomTheme>(
  DEFAULT_CUSTOM_THEME,
  'currentCustomThemeAtom',
).pipe(
  withLocalStorage({
    key: STORAGE_KEY,
  }),
)

export const currentCustomUiThemeAtom = atom((ctx) => {
  const currentCustomTheme = ctx.spy(currentCustomThemeAtom)

  return UITheme.generateTheme(
    {
      appearance: currentCustomTheme.appearance,
      appearanceColor: currentCustomTheme.appearanceColor,
      ...currentCustomTheme.customColors,
    },
    Theme.themesMap[currentCustomTheme.baseTheme].uiTheme,
  )
}, 'currentCustomUiThemeAtom')

export const patchCustomThemeColorsAction = action(
  (ctx, patch: Partial<CustomThemeColors>) => {
    currentCustomThemeAtom(
      ctx,
      (prev) =>
        new CustomTheme({
          baseTheme: prev.baseTheme,
          customColors: patchCustomThemeColors(prev.customColors, patch),
          appearance: prev.appearance,
          appearanceColor: prev.appearanceColor,
        }),
    )
  },
  'patchCustomThemeColorsAction',
)

export const setCustomBaseThemeAction = action((ctx, baseTheme: ThemeId) => {
  const baseUiTheme = Theme.themesMap[baseTheme].uiTheme

  currentCustomThemeAtom(
    ctx,
    () =>
      new CustomTheme({
        baseTheme,
        customColors: customThemeColorsFromBaseTheme(baseUiTheme),
        appearance: baseUiTheme.appearance,
        appearanceColor: baseUiTheme.appearanceColor,
      }),
  )
}, 'setCustomBaseThemeAction')

export const setCustomAppearanceAction = action(
  (ctx, appearance: Appearance) => {
    currentCustomThemeAtom(
      ctx,
      (prev) =>
        new CustomTheme({
          ...prev,
          appearance,
        }),
    )
  },
  'setCustomAppearanceAction',
)

export const setCustomAppearanceColorAction = action(
  (ctx, appearanceColor: string) => {
    currentCustomThemeAtom(
      ctx,
      (prev) => new CustomTheme({ ...prev, appearanceColor }),
    )
  },
  'setCustomAppearanceColorAction',
)
